import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Language } from '../types';
import { MOCK_LANGUAGES } from '../constants';
import { translations } from '../i18n/translations';
import { api, isApiConnected } from '../utils/apiClient';
import { mapLanguageFromDb, mapLanguageToDb, getErrorMessage } from '../utils/dataMappers';

interface LanguageContextType {
  languages: Language[];
  activeLanguages: Language[];
  defaultLanguage: Language;
  currentLanguage: Language;
  changeLanguage: (code: string) => void;
  t: (key: string, variables?: Record<string, string | number>) => string;
  dir: 'ltr' | 'rtl';
  isLoading: boolean;

  // Admin functions
  addLanguage: (lang: Language) => Promise<void>;
  updateLanguage: (code: string, updates: Partial<Language>) => Promise<void>;
  deleteLanguage: (code: string) => Promise<void>;
  setAsDefault: (code: string) => Promise<void>;
  toggleStatus: (code: string) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [currentLangCode, setCurrentLangCode] = useState('en');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // --- Data Fetching ---
  const fetchLanguages = async (): Promise<Language[]> => {
    if (!isApiConnected) {
      setLanguages(MOCK_LANGUAGES);
      return MOCK_LANGUAGES;
    }

    try {
      const { data, error } = await api.from('languages').select('*').order('name');

      if (error) {
        throw error;
      }

      if (Array.isArray(data) && data.length > 0) {
        const mapped = data.map(item => mapLanguageFromDb(item));
        setLanguages(mapped);
        return mapped;
      } else {
        // Fallback to mocks if table is empty or data is invalid
        setLanguages(MOCK_LANGUAGES);
        return MOCK_LANGUAGES;
      }
    } catch (err: any) {
      const readableError = getErrorMessage(err);
      console.warn("Cloud Language retrieval disabled or failed, using local configuration:", readableError);

      // Gracious fallback to mock data on network/fetch failure
      setLanguages(MOCK_LANGUAGES);
      return MOCK_LANGUAGES;
    }
  };

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      const fetched = await fetchLanguages();

      const defaultLang = fetched.find(l => l.isDefault) || (fetched.length > 0 ? fetched[0] : MOCK_LANGUAGES[0]);

      // 1. Check LocalStorage
      const savedCode = localStorage.getItem('user_language');
      if (savedCode) {
        const found = fetched.find(l => l.code === savedCode && l.isActive);
        if (found) {
          setCurrentLangCode(found.code);
          setIsLoading(false);
          return;
        }
      }

      // 2. Browser Detection (Automatic)
      try {
        const browserLang = navigator.language || (navigator as any).userLanguage;
        if (browserLang) {
          // Attempt 1: Exact match (e.g. 'en-US')
          const exactMatch = fetched.find(l => l.code.toLowerCase() === browserLang.toLowerCase() && l.isActive);
          if (exactMatch) {
            setCurrentLangCode(exactMatch.code);
          } else {
            // Attempt 2: Base code match (e.g. 'en' from 'en-US')
            const shortCode = browserLang.split('-')[0];
            const shortMatch = fetched.find(l => l.code.toLowerCase() === shortCode.toLowerCase() && l.isActive);
            if (shortMatch) {
              setCurrentLangCode(shortMatch.code);
            } else {
              // Fallback to default
              setCurrentLangCode(defaultLang.code);
            }
          }
        } else {
          setCurrentLangCode(defaultLang.code);
        }
      } catch (e) {
        console.warn("Language auto-detection failed, using default", e);
        setCurrentLangCode(defaultLang.code);
      }

      setIsLoading(false);
    };

    initialize();
  }, []);

  const activeLanguages = languages.filter(l => l.isActive);
  const defaultLanguage = languages.find(l => l.isDefault) || (languages.length > 0 ? languages[0] : MOCK_LANGUAGES[0]);
  const currentLanguage = languages.find(l => l.code === currentLangCode) || defaultLanguage;
  const dir = currentLanguage?.direction || 'ltr';

  useEffect(() => {
    if (dir && currentLangCode) {
      document.documentElement.dir = dir;
      document.documentElement.lang = currentLangCode;
    }
  }, [dir, currentLangCode]);

  const t = (key: string, variables?: Record<string, string | number>): string => {
    const keys = key.split('.');
    let value: any = translations[currentLangCode as keyof typeof translations] || translations['en'];

    for (const k of keys) {
      value = value?.[k];
    }

    let result = (value || (currentLangCode !== 'en' ? (() => {
      let fallback: any = translations['en'];
      for (const k of keys) fallback = fallback?.[k];
      return fallback;
    })() : null) || key) as string;

    if (variables && typeof result === 'string') {
      Object.entries(variables).forEach(([vKey, vValue]) => {
        result = result.replace(new RegExp(`{${vKey}}`, 'g'), String(vValue));
      });
    }

    return result;
  };

  const changeLanguage = (code: string) => {
    setCurrentLangCode(code);
    localStorage.setItem('user_language', code);

    // SEO: Sync URL with language choice for public pages
    if (!window.location.pathname.startsWith('/admin') && !window.location.pathname.startsWith('/portal')) {
      const pathSegments = location.pathname.split('/').filter(Boolean);
      const matched = languages.find(l => l.code.toLowerCase() === pathSegments[0]?.toLowerCase());
      const purePathArr = matched ? pathSegments.slice(1) : pathSegments;
      const purePath = purePathArr.join('/');

      const targetLang = languages.find(l => l.code === code);
      const isDefault = targetLang?.isDefault;

      const newPath = isDefault ? `/${purePath}` : `/${code}/${purePath}`;

      if (location.pathname !== newPath) {
        navigate(newPath);
      }
    }
  };

  // --- Admin Persistence Functions ---

  const addLanguage = async (lang: Language) => {
    try {
      const payload = mapLanguageToDb(lang);
      const { error } = await api.from('languages').insert(payload);
      if (error) throw error;
      setLanguages(prev => [...prev, lang]);
    } catch (err: any) {
      const msg = getErrorMessage(err);
      console.error("Error adding language:", msg);
      alert("Failed to add language: " + msg);
    }
  };

  const updateLanguage = async (code: string, updates: Partial<Language>) => {
    try {
      const { error } = await api.from('languages').update(mapLanguageToDb(updates)).eq('code', code);
      if (error) throw error;
      setLanguages(prev => prev.map(l => l.code === code ? { ...l, ...updates } : l));
    } catch (err: any) {
      const msg = getErrorMessage(err);
      console.error("Error updating language:", msg);
      alert("Failed to update language: " + msg);
    }
  };

  const deleteLanguage = async (code: string) => {
    try {
      const { error } = await api.from('languages').delete().eq('code', code);
      if (error) throw error;
      setLanguages(prev => prev.filter(l => l.code !== code));
    } catch (err: any) {
      const msg = getErrorMessage(err);
      console.error("Error deleting language:", msg);
      alert("Failed to delete language: " + msg);
    }
  };

  const setAsDefault = async (code: string) => {
    try {
      // 1. Set all to non-default. api requires a filter for updates.
      const { error: resetError } = await api
        .from('languages')
        .update({ is_default: false })
        .neq('code', '');

      if (resetError) throw resetError;

      // 2. Set target to default and active
      const { error: setError } = await api
        .from('languages')
        .update({ is_default: true, is_active: true })
        .eq('code', code);

      if (setError) throw setError;

      setLanguages(prev => prev.map(l => ({
        ...l,
        isDefault: l.code === code,
        isActive: l.code === code ? true : l.isActive
      })));
    } catch (err: any) {
      const msg = getErrorMessage(err);
      console.error("Error setting default language:", msg);
      alert("Failed to set default: " + msg);
    }
  };

  const toggleStatus = async (code: string) => {
    const lang = languages.find(l => l.code === code);
    if (!lang || lang.isDefault) return;

    const nextStatus = !lang.isActive;
    try {
      const { error } = await api.from('languages').update({ is_active: nextStatus }).eq('code', code);
      if (error) throw error;
      setLanguages(prev => prev.map(l => l.code === code ? { ...l, isActive: nextStatus } : l));
    } catch (err: any) {
      const msg = getErrorMessage(err);
      console.error("Error toggling status:", msg);
      alert("Failed to update status: " + msg);
    }
  };

  return (
    <LanguageContext.Provider value={{
      languages,
      activeLanguages,
      defaultLanguage,
      currentLanguage,
      changeLanguage,
      t,
      dir,
      isLoading,
      addLanguage,
      updateLanguage,
      deleteLanguage,
      setAsDefault,
      toggleStatus
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguages = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguages must be used within a LanguageProvider');
  }
  return context;
};

